class VideoCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.itemsPerView = 2;
    this.maxIndex = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.dotsContainer = this.querySelector('[data-dots]');
    this.items = this.querySelectorAll('.video-carousel__item');
    this.videos = this.querySelectorAll('video.video-carousel__video');

    if (!this.items.length) return;

    this.columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;

    this.updateItemsPerView();
    this.calculateMaxIndex();
    this.setupVideoPlayback();
    this.setupNavigation();
    this.createDots();
    this.updateNavigation();

    window.addEventListener('resize', () => {
      this.updateItemsPerView();
      this.calculateMaxIndex();
      this.createDots();
      this.updateSlidePosition();
      this.updateNavigation();
    });
  }

  updateItemsPerView() {
    this.itemsPerView = window.innerWidth >= 750 ? this.columnsDesktop : 2;
  }

  calculateMaxIndex() {
    this.maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    if (this.currentIndex > this.maxIndex) this.currentIndex = this.maxIndex;
  }

  setupVideoPlayback() {
    if (!this.videos.length) return;

    this.videos.forEach((video) => {
      const tryPlay = () => video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });

      video.addEventListener('loadedmetadata', tryPlay);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              tryPlay();
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(video);
    });
  }

  setupNavigation() {
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.prev());
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.next());

    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.next() : this.prev();
      }
      isDragging = false;
    });
  }

  createDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const numDots = this.maxIndex + 1;

    for (let i = 0; i < numDots; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === this.currentIndex) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    [...this.dotsContainer.children].forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlidePosition();
      this.updateNavigation();
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateSlidePosition();
      this.updateNavigation();
    }
  }

  goToSlide(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.updateSlidePosition();
    this.updateNavigation();
  }

  updateSlidePosition() {
    const itemWidth = this.items[0].offsetWidth;
    const gap = 12;
    const offset = -(this.currentIndex * (itemWidth + gap));
    this.track.style.transform = `translateX(${offset}px)`;
  }

  updateNavigation() {
    if (this.prevButton) this.prevButton.disabled = this.currentIndex === 0;
    if (this.nextButton) this.nextButton.disabled = this.currentIndex >= this.maxIndex;
    this.updateDots();
  }
}

customElements.define('video-carousel', VideoCarousel);
